import type { Theme } from '@mui/material/styles';

export function hexToHSL(hex: string): { h: number; s: number; l: number } {
    const r = parseInt(hex.substring(1, 3), 16) / 255;
    const g = parseInt(hex.substring(3, 5), 16) / 255;
    const b = parseInt(hex.substring(5, 7), 16) / 255;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0, s;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    } else {
        s = 0;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
}

export function hslToHex(h: number, s: number, l: number): string {
    s /= 100;
    l /= 100;
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) [r, g, b] = [c, x, 0];
    else if (h >= 60 && h < 120) [r, g, b] = [x, c, 0];
    else if (h >= 120 && h < 180) [r, g, b] = [0, c, x];
    else if (h >= 180 && h < 240) [r, g, b] = [0, x, c];
    else if (h >= 240 && h < 300) [r, g, b] = [x, 0, c];
    else[r, g, b] = [c, 0, x];

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

export function generateShades(mainHex: string) {
    const { h, s, l } = hexToHSL(mainHex);
    return {
        lighter: hslToHex(h, s, l + 30),
        light: hslToHex(h, s, l + 15),
        main: mainHex,
        dark: hslToHex(h, s, l - 15),
        darker: hslToHex(h, s, l - 30),
        contrastText: '#FFFFFF'
    };
}

/** WCAG 2.x relative luminance of a #rrggbb colour. */
export function relativeLuminance(hex: string): number {
    const channel = (offset: number) => {
        const v = parseInt(hex.substring(offset, offset + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG contrast ratio between two #rrggbb colours (1–21). */
export function contrastRatio(a: string, b: string): number {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** WCAG AA minimum for body text and the bar every palette colour used as text must clear. */
export const TEXT_CONTRAST_MIN = 4.5;

/**
 * Dark-mode shades for a brand colour. MUI uses `main` for text as well as fills (links, outlined
 * buttons, selected tabs), so a saturated colour tuned for white surfaces disappears on dark paper.
 * `main` is lightened in the same hue until it reads at AA contrast on the surface — the AttendIn
 * violet lands on its own `primary.light` — and `contrastText` becomes a deep tone of the same
 * family whenever white would fail on the lighter fill.
 */
export function generateDarkModeShades(mainHex: string, surfaceHex: string) {
    const { h, s, l } = hexToHSL(mainHex);
    let lightness = l;
    let main = mainHex;
    while (contrastRatio(main, surfaceHex) < TEXT_CONTRAST_MIN && lightness < 95) {
        lightness += 1;
        main = hslToHex(h, s, lightness);
    }

    const deep = hslToHex(h, s, Math.min(l, 15));
    const contrastText = contrastRatio('#FFFFFF', main) >= TEXT_CONTRAST_MIN ? '#FFFFFF' : deep;

    return {
        lighter: hslToHex(h, s, Math.min(lightness + 30, 97)),
        light: hslToHex(h, s, Math.min(lightness + 15, 95)),
        main,
        dark: hslToHex(h, s, lightness - 15),
        darker: hslToHex(h, s, lightness - 30),
        contrastText
    };
}

export function getCommunityFallbackGradient(theme: Theme): string {
    return `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary?.main ?? '#764ba2'} 100%)`;
}